import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { C, audit, WA } from '../constants';
import { Btn, Input, Card, Toast, MetodoCard, StepIndicator } from '../components/Atoms';
import LOGO from '../assets/logo';

// ─── REGISTRO (3 PASOS) ────────────────────────────────────
function Registro({ onLogin, onAviso, onTerminos }) {
  // Recuperar datos guardados si el usuario cerró el navegador
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem('tmx_registro') || '{}'); } catch { return {}; } })();
  const [step, setStep] = useState(saved.step || 1);
  const [nombre, setNombre] = useState(saved.nombre || '');
  const [email, setEmail] = useState(saved.email || '');
  const [telefono, setTelefono] = useState(saved.telefono || '');
  const [password, setPassword] = useState('');
  const [metodoPago, setMetodoPago] = useState(saved.metodoPago || 'spei');
  const [metodoCobro, setMetodoCobro] = useState(saved.metodoCobro || 'spei');
  const [clabe, setClabe] = useState(saved.clabe || '');
  const [banco, setBanco] = useState(saved.banco || '');
  const [nombreTitular, setNombreTitular] = useState(saved.nombreTitular || '');
  const [dirCP, setDirCP] = useState(saved.dirCP || '');
  const [dirEstado, setDirEstado] = useState(saved.dirEstado || '');
  const [dirCiudad, setDirCiudad] = useState(saved.dirCiudad || '');
  const [dirColonia, setDirColonia] = useState(saved.dirColonia || '');
  const [dirCalle, setDirCalle] = useState(saved.dirCalle || '');
  const [dirNoExt, setDirNoExt] = useState(saved.dirNoExt || '');
  const [dirNoInt, setDirNoInt] = useState(saved.dirNoInt || '');
  const [coloniaOpciones, setColoniaOpciones] = useState([]);
  const [buscandoCP, setBuscandoCP] = useState(false);
  const [codigoReferido, setCodigoReferido] = useState(saved.codigoReferido || '');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [codigoOTP, setCodigoOTP] = useState('');
  const [enviandoOTP, setEnviandoOTP] = useState(false);
  const [otpEnviado, setOtpEnviado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ msg: '', type: '' });

  const showToast = (msg, type = 'info') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 4000); };

  const sanitizar = (str) => (str || '').replace(/<[^>]*>/g, '').replace(/[<>"'`]/g, '').trim().slice(0, 200);

  const validarPassword = (p) => {
    if (p.length < 8) return 'Mínimo 8 caracteres';
    if (!/[A-Z]/.test(p)) return 'Debe tener al menos una mayúscula';
    if (!/[0-9]/.test(p)) return 'Debe tener al menos un número';
    if (!/[!@#$%^&*]/.test(p)) return 'Debe incluir un símbolo (!@#$%^&*)';
    return null;
  };

  const guardarProgreso = (nextStep) => {
    try { sessionStorage.setItem('tmx_registro', JSON.stringify({ step: nextStep, nombre, email, telefono, metodoPago, metodoCobro, clabe, banco, nombreTitular, dirCP, dirEstado, dirCiudad, dirColonia, dirCalle, dirNoExt, dirNoInt, codigoReferido })); } catch {}
  };

  const paso1 = () => {
    if (!nombre || !email || !telefono || !password) { showToast('Llena todos los campos', 'error'); return; }
    if (!/^[1-9]\d{9}$/.test(telefono)) { showToast('Teléfono: 10 dígitos, número mexicano válido', 'error'); return; }
    const e = validarPassword(password); if (e) { showToast(e, 'error'); return; }
    guardarProgreso(2);
    setStep(2);
  };

  const validarCLABE = (c) => {
    if (!/^\d{18}$/.test(c)) return 'La CLABE debe tener exactamente 18 dígitos';
    const pesos = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
    let suma = 0;
    for (let i = 0; i < 17; i++) suma += (parseInt(c[i]) * pesos[i]) % 10;
    const dc = (10 - (suma % 10)) % 10;
    if (parseInt(c[17]) !== dc) return 'CLABE inválida — verifica el número';
    return null;
  };

  const paso2 = () => {
    if (metodoCobro === 'spei' && !clabe) {
      showToast('Ingresa tu CLABE para cobrar por transferencia', 'error'); return;
    }
    if (metodoCobro === 'spei') {
      const errClabe = validarCLABE(clabe);
      if (errClabe) { showToast(errClabe, 'error'); return; }
      if (!banco) { showToast('Selecciona tu banco', 'error'); return; }
    }
    guardarProgreso(3);
    setStep(3);
  };

  const estadosMX = ['Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua','Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato','Guerrero','Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca','Puebla','Querétaro','Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas','Tlaxcala','Veracruz','Yucatán','Zacatecas'];

  const buscarCP = async () => {
    if (dirCP.length !== 5) return;
    setBuscandoCP(true);
    try {
      const cpController = new AbortController();
      const cpTimeout = setTimeout(() => cpController.abort(), 8000);
      // API principal: sepomex (gratis, sin token)
      const res = await fetch(`https://sepomex.icalialabs.com/api/v1/zip_codes?zip_code=${dirCP}`, { signal: cpController.signal });
      clearTimeout(cpTimeout);
      const data = await res.json();
      if (data.zip_codes && data.zip_codes.length > 0) {
        const z = data.zip_codes[0];
        setDirEstado(z.d_estado || '');
        setDirCiudad(z.d_mnpio || '');
        const cols = data.zip_codes.map(zc => zc.d_asenta).filter(Boolean);
        setColoniaOpciones(cols);
        if (cols.length === 1) setDirColonia(cols[0]);
      } else {
        // Fallback: copomex
        const res2 = await fetch(`https://api.copomex.com/query/info_cp/${dirCP}?token=demoapitoken`);
        const data2 = await res2.json();
        if (data2 && data2.error === false && data2.response) {
          const r = Array.isArray(data2.response) ? data2.response[0] : data2.response;
          setDirEstado(r.estado || '');
          setDirCiudad(r.municipio || r.ciudad || '');
          const colonias = Array.isArray(data2.response)
            ? data2.response.map(d => d.asentamiento).filter(Boolean)
            : [r.asentamiento].filter(Boolean);
          setColoniaOpciones(colonias);
          if (colonias.length === 1) setDirColonia(colonias[0]);
        } else {
          showToast('Código postal no encontrado. Llena los campos manualmente.', 'info');
        }
      }
    } catch(e) {
      showToast('No se pudo buscar el CP. Llena los campos manualmente.', 'info');
    }
    setBuscandoCP(false);
  };

  const enviarOTP = async () => {
    if (!dirCP || !dirEstado || !dirCiudad || !dirColonia || !dirCalle || !dirNoExt) { showToast('Completa todos los campos de dirección', 'error'); return; }
    if (!aceptaTerminos) { showToast('Debes aceptar los Términos y el Aviso de Privacidad', 'error'); return; }
    setEnviandoOTP(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw error;
      setOtpEnviado(true);
      setStep(4);
      showToast('Código enviado a ' + email, 'success');
    } catch (err) { showToast('Error al enviar código: ' + err.message, 'error'); }
    setEnviandoOTP(false);
  };

  const verificarOTP = async () => {
    if (!codigoOTP || codigoOTP.length < 6) { showToast('Ingresa el código de 6 dígitos', 'error'); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: codigoOTP, type: 'email' });
      if (error) throw error;
      const userId = data.user.id;

      // Actualizar contraseña del usuario
      await supabase.auth.updateUser({ password });

      // Generar número de cliente único (6 dígitos)
      const codigoRef = String(crypto.getRandomValues(new Uint32Array(1))[0] % 900000 + 100000);

      const datosUsuario = {
        id: userId, nombre: sanitizar(nombre), telefono: '+52' + telefono.replace(/[^0-9]/g, '').slice(0, 10),
        nivel: 'Bronce', tandas_completadas: 0, rol: 'user',
        codigo_ref: codigoRef,
        metodo_pago: metodoPago, metodo_cobro: metodoCobro,
        clabe: metodoCobro === 'spei' ? clabe : null,
        banco: metodoCobro === 'spei' ? banco : null,
        nombre_titular: metodoCobro === 'spei' ? nombre : null,
        direccion_calle: dirCalle, no_exterior: dirNoExt, no_interior: dirNoInt || null,
        colonia: dirColonia, ciudad: dirCiudad, estado: dirEstado, codigo_postal: dirCP,
        referido_por: codigoReferido ? codigoReferido.toUpperCase().replace(/[^A-Z0-9]/g, '') : null,
      };
      // Verificar si ya existe para no sobrescribir datos (nivel, tandas_completadas, etc.)
      const { data: existente } = await supabase.from('usuarios').select('id').eq('id', userId).maybeSingle();
      if (existente) {
        // Ya existe — no sobrescribir, solo actualizar campos de contacto
        const { error: e2 } = await supabase.from('usuarios').update({
          telefono: datosUsuario.telefono,
          metodo_pago: datosUsuario.metodo_pago, metodo_cobro: datosUsuario.metodo_cobro,
          clabe: datosUsuario.clabe, banco: datosUsuario.banco, nombre_titular: datosUsuario.nombre_titular,
        }).eq('id', userId);
        if (e2) throw new Error('Error actualizando tu cuenta: ' + e2.message);
      } else {
        const { error: e2 } = await supabase.from('usuarios').insert(datosUsuario);
        if (e2) throw new Error('Error al crear tu cuenta: ' + e2.message);
      }
      await audit('registro', { email: email.toLowerCase() });
      try { sessionStorage.removeItem('tmx_registro'); } catch {}
      showToast('¡Bienvenido a TandaClick! 🎉', 'success');
    } catch (err) { showToast(err.message, 'error'); setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.gray50, padding: '32px 20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Toast msg={toast.msg} type={toast.type} />
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img src={LOGO} alt="TandaClick" style={{ height: 80, width: 'auto', objectFit: 'contain' }} />
        
        <div style={{ fontSize: 13, color: C.textMd, marginTop: 4 }}>Crea tu cuenta gratis</div>
      </div>

      {/* PASO 1 */}
      {step === 1 && (
        <Card>
          <StepIndicator step={1} />
          <div style={{ fontSize: 13, fontWeight: 800, color: C.primary, marginBottom: 16, textAlign: 'center' }}>Paso 1 — Datos básicos</div>
          <Input label="Nombre completo" value={nombre} onChange={setNombre} placeholder="Juan Pérez" />
          <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="juan@email.com" />
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>WhatsApp (10 dígitos)</div>
            <input type="tel" value={telefono} onChange={e => setTelefono(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="4771234567" maxLength={10}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
            <div style={{ fontSize: 11, color: C.textLt, marginTop: 4 }}>Recibirás recordatorios de pago aquí</div>
          </div>
          <Input label="Contraseña" value={password} onChange={setPassword} type="password" placeholder="Mín. 8 caracteres" note="Mayúscula, número y símbolo (!@#$%^&*)" />
          <Btn onClick={paso1}>Continuar →</Btn>
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: C.textMd }}>
            ¿Ya tienes cuenta?{' '}
            <span onClick={onLogin} style={{ color: C.primary, fontWeight: 700, cursor: 'pointer' }}>Inicia sesión</span>
          </div>
        </Card>
      )}

      {/* PASO 2 */}
      {step === 2 && (
        <Card>
          <StepIndicator step={2} />
          <div style={{ fontSize: 13, fontWeight: 800, color: C.primary, marginBottom: 4, textAlign: 'center' }}>Paso 2 — Método de pago y cobro</div>
          <div style={{ fontSize: 12, color: C.textMd, textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>¿Cómo vas a depositar y recibir tu tanda?</div>

          <div style={{ fontSize: 12, fontWeight: 800, color: C.textMd, marginBottom: 10 }}>¿CÓMO VAS A PAGAR TU TANDA?</div>
          <MetodoCard icon="🏦" titulo="Transferencia SPEI" descripcion="Deposita desde tu banco o app bancaria a la CLABE de la tanda." seleccionado={metodoPago === 'spei'} onClick={() => setMetodoPago('spei')} />
          <MetodoCard icon="🏪" titulo="Pago en OXXO" descripcion="Deposita en cualquier OXXO con el número de tarjeta de tu tanda." seleccionado={metodoPago === 'oxxo'} onClick={() => setMetodoPago('oxxo')} color={C.oxxo} />

          <div style={{ borderTop: `1px solid ${C.gray200}`, margin: '20px 0' }} />

          <div style={{ fontSize: 12, fontWeight: 800, color: C.textMd, marginBottom: 10 }}>¿CÓMO QUIERES COBRAR TU TANDA?</div>
          <MetodoCard icon="🏦" titulo="Transferencia SPEI" descripcion="Cuando sea tu turno, te depositamos directo a tu cuenta bancaria." seleccionado={metodoCobro === 'spei'} onClick={() => setMetodoCobro('spei')} />
          <MetodoCard icon="📱" titulo="Cobrar en OXXO con QR" descripcion="Cuando sea tu turno, te enviamos un código QR para cobrar en cualquier OXXO." seleccionado={metodoCobro === 'oxxo'} onClick={() => setMetodoCobro('oxxo')} color={C.oxxo} />

          {metodoCobro === 'spei' && (
            <div style={{ marginTop: 16, padding: '16px', background: C.primaryLt, borderRadius: 14, border: `1px solid ${C.primaryMd}` }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.primary, marginBottom: 12 }}>🔒 Datos para tu cobro por transferencia</div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>CLABE (18 dígitos)</div>
                <input type="tel" value={clabe} onChange={e => setClabe(e.target.value.replace(/\D/g, '').slice(0, 18))}
                  placeholder="646180157000000001" maxLength={18}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Banco</div>
                <select value={banco} onChange={e => setBanco(e.target.value)}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${banco ? C.primary : C.gray200}`, fontSize: 14, color: banco ? C.text : C.textLt, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <option value="">— Selecciona tu banco —</option>
                  {['BBVA','Banorte','Santander','HSBC','Scotiabank','Citibanamex','Banco Azteca','BanCoppel','Spin by OXXO','Nu','Rappi','Mercado Pago','Hey Banco','Inbursa','Banregio','Afirme','BanBajio','Otro'].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Nombre del titular</div>
                <input value={nombre} disabled
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.gray50, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }} />
                <div style={{ fontSize: 11, color: C.textLt, marginTop: 4 }}>Se usa el nombre que registraste en el paso 1. Si no es correcto, regresa a corregirlo.</div>
              </div>
              <div style={{ fontSize: 11, color: C.textMd, lineHeight: 1.6, marginTop: 4 }}>🔐 Validamos que el nombre del titular coincida antes de realizar cualquier depósito a tu cuenta.</div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <Btn onClick={paso2}>Continuar →</Btn>
            <Btn onClick={() => setStep(1)} variant="ghost" style={{ marginTop: 8 }}>← Paso anterior</Btn>
          </div>
        </Card>
      )}

      {/* PASO 3 */}
      {step === 3 && (
        <Card>
          <StepIndicator step={3} />
          <div style={{ fontSize: 13, fontWeight: 800, color: C.primary, marginBottom: 4, textAlign: 'center' }}>Paso 3 — Tu dirección</div>
          <div style={{ background: C.primaryLt, borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.primary, marginBottom: 4 }}>🏠 ¿Por qué pedimos tu dirección?</div>
            <div style={{ fontSize: 12, color: C.textMd, lineHeight: 1.6 }}>Es necesaria para verificar tu identidad y cumplir con la regulación financiera.</div>
          </div>

          {/* Código postal */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Código postal</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="tel" value={dirCP} onChange={e => setDirCP(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="37000" maxLength={5}
                style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
              <button onClick={buscarCP} disabled={dirCP.length !== 5 || buscandoCP}
                style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: dirCP.length === 5 ? C.primary : C.gray200, color: C.white, fontSize: 13, fontWeight: 700, cursor: dirCP.length === 5 ? 'pointer' : 'default', fontFamily: "'Plus Jakarta Sans', sans-serif", flexShrink: 0 }}>
                {buscandoCP ? '...' : 'Buscar'}
              </button>
            </div>
          </div>

          {/* Estado — dropdown con los 32 estados */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Estado</div>
            <select value={dirEstado} onChange={e => setDirEstado(e.target.value)}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: dirEstado ? C.text : C.textLt, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <option value="">— Selecciona tu estado —</option>
              {estadosMX.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {/* Ciudad — editable */}
          <Input label="Ciudad / Municipio" value={dirCiudad} onChange={setDirCiudad} placeholder="Ej: León" />

          {/* Colonia — dropdown si hay opciones, input si no */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Colonia</div>
            {coloniaOpciones.length > 0 ? (
              <select value={dirColonia} onChange={e => setDirColonia(e.target.value)}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.primary}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <option value="">— Selecciona tu colonia —</option>
                {coloniaOpciones.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input value={dirColonia} onChange={e => setDirColonia(e.target.value)} placeholder="Ej: Centro"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
            )}
          </div>

          {/* Calle */}
          <Input label="Calle" value={dirCalle} onChange={setDirCalle} placeholder="Ej: Av. Reforma" />

          {/* No. Ext y No. Int */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>No. Exterior</div>
              <input value={dirNoExt} onChange={e => setDirNoExt(e.target.value)} placeholder="123"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>No. Interior <span style={{ fontWeight: 400, color: C.textLt }}>(opcional)</span></div>
              <input value={dirNoInt} onChange={e => setDirNoInt(e.target.value)} placeholder="4-A"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
            </div>
          </div>

          {/* Código de referido */}
          <div style={{ background: C.gray50, borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>¿Tienes un código de referido? <span style={{ fontWeight: 400, color: C.textLt }}>(opcional)</span></div>
            <input value={codigoReferido} onChange={e => setCodigoReferido(e.target.value.toUpperCase().slice(0, 8))}
              placeholder="Ej: TC865416"
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.05em' }}
              onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.gray200} />
            <div style={{ fontSize: 11, color: C.textLt, marginTop: 4 }}>Si alguien te invitó, ingresa su código aquí</div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={aceptaTerminos} onChange={e => setAceptaTerminos(e.target.checked)}
                style={{ marginTop: 2, width: 18, height: 18, accentColor: C.primary, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: C.textMd, lineHeight: 1.6 }}>
                He leído y acepto los{' '}
                <span onClick={onTerminos} style={{ color: C.primary, fontWeight: 700, cursor: 'pointer' }}>Términos y Condiciones</span>
                {' '}y el{' '}
                <span onClick={onAviso} style={{ color: C.primary, fontWeight: 700, cursor: 'pointer' }}>Aviso de Privacidad</span>
                , incluyendo el reporte al Buró de Crédito.
              </span>
            </label>
          </div>
          <Btn onClick={enviarOTP} disabled={enviandoOTP || !aceptaTerminos}>{enviandoOTP ? 'Enviando código...' : '📧 Verificar mi correo'}</Btn>
          <Btn onClick={() => setStep(2)} variant="ghost" style={{ marginTop: 8 }}>← Paso anterior</Btn>
        </Card>
      )}

      {/* PASO 4 — Verificar código */}
      {step === 4 && (
        <Card>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>📧</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 4 }}>Verifica tu correo</div>
            <div style={{ fontSize: 13, color: C.textMd, lineHeight: 1.6 }}>
              Enviamos un código de 6 dígitos a<br/><strong>{email}</strong>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Código de verificación</div>
            <input type="tel" value={codigoOTP} onChange={e => setCodigoOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" maxLength={6}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: `2px solid ${C.primary}`, fontSize: 28, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace', fontWeight: 900, textAlign: 'center', letterSpacing: '0.3em' }}
            />
          </div>
          <Btn onClick={verificarOTP} disabled={loading || codigoOTP.length < 6}>{loading ? 'Verificando...' : '✅ Verificar y crear cuenta'}</Btn>
          <button onClick={enviarOTP} disabled={enviandoOTP} style={{ width: '100%', padding: '12px 0', marginTop: 10, background: 'none', border: 'none', color: C.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {enviandoOTP ? 'Enviando...' : '¿No llegó? Reenviar código'}
          </button>
          <Btn onClick={() => setStep(3)} variant="ghost" style={{ marginTop: 4 }}>← Paso anterior</Btn>
        </Card>
      )}
    </div>
  );
}

// ─── LOGIN ─────────────────────────────────────────────────
function Login({ onRegistro, onLanding }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recordarme, setRecordarme] = useState(false);
  const [intentosFallidos, setIntentosFallidos] = useState(0);
  const [bloqueadoHasta, setBloqueadoHasta] = useState(null);
  const [verPass, setVerPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modoRecuperar, setModoRecuperar] = useState(false);
  const [toast, setToast] = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'info') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 4000); };

  useEffect(() => {
    try {
      const emailGuardado = localStorage.getItem('tmx_email_recordado');
      if (emailGuardado) { setEmail(emailGuardado); setRecordarme(true); }
    } catch(e) {}
  }, []); // eslint-disable-line

  const toggleRecordarme = () => {
    const nuevo = !recordarme;
    setRecordarme(nuevo);
    try { if (!nuevo) try { localStorage.removeItem('tmx_email_recordado'); } catch(e) {} } catch(e) {}
  };

  const login = async () => {
    if (!email || !password) { showToast('Llena todos los campos', 'error'); return; }
    if (bloqueadoHasta && Date.now() < bloqueadoHasta) {
      const mins = Math.ceil((bloqueadoHasta - Date.now()) / 60000);
      showToast(`Demasiados intentos. Espera ${mins} minuto${mins > 1 ? 's' : ''} e intenta de nuevo`, 'error'); return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      await audit('login', { email: email.toLowerCase() });
      setIntentosFallidos(0); setBloqueadoHasta(null);
      // Recordar email si está activado
      if (recordarme) {
        try { localStorage.setItem('tmx_email_recordado', email.toLowerCase().trim()); } catch(e) {}
      } else {
        try { localStorage.removeItem('tmx_email_recordado'); } catch(e) {}
      }
    }
    if (error) {
      const nuevosIntentos = intentosFallidos + 1;
      setIntentosFallidos(nuevosIntentos);
      if (nuevosIntentos >= 5) {
        const hasta = Date.now() + 5 * 60 * 1000;
        setBloqueadoHasta(hasta);
        await audit('login_bloqueado', { email: email.toLowerCase(), intentos: nuevosIntentos });
        showToast('Demasiados intentos fallidos. Cuenta bloqueada por 5 minutos', 'error');
      } else {
        showToast(`Email o contraseña incorrectos (intento ${nuevosIntentos}/5)`, 'error');
      } setLoading(false);
    }
  };


  const recuperar = async () => {
    if (!email) { showToast('Ingresa tu email primero', 'error'); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://tandaclick.com',
    });
    setLoading(false);
    if (error) { showToast('Error: ' + error.message, 'error'); }
    else { showToast('Revisa tu correo para restablecer tu contraseña', 'success'); setModoRecuperar(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.gray50, padding: '40px 20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Toast msg={toast.msg} type={toast.type} />
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <img src={LOGO} alt="TandaClick" style={{ height: 80, width: 'auto', objectFit: 'contain' }} />
        <div style={{ fontSize: 14, color: C.textMd, marginTop: 4 }}>{modoRecuperar ? 'Recuperar contraseña' : 'Bienvenido de vuelta'}</div>
      </div>
      <Card>
        <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="juan@email.com" />
        {!modoRecuperar && (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Contraseña</div>
              <div style={{ position: 'relative' }}>
                <input
                  type={verPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  onKeyDown={e => e.key === 'Enter' && login()}
                  style={{ width: '100%', padding: '11px 44px 11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  onFocus={e => e.target.style.borderColor = C.primary}
                  onBlur={e => e.target.style.borderColor = C.gray200}
                />
                {/* Ojo discreto — SVG gris simple */}
                <button onClick={() => setVerPass(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.gray400, display: 'flex', alignItems: 'center' }}>
                  {verPass ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {/* Recordarme + olvidé contraseña */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={recordarme}
                  onChange={toggleRecordarme}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: C.textMd, fontWeight: 600 }}>Recordarme</span>
              </label>
              <span onClick={() => setModoRecuperar(true)} style={{ fontSize: 13, color: C.primary, fontWeight: 700, cursor: 'pointer' }}>
                ¿Olvidaste tu contraseña?
              </span>
            </div>
            <Btn onClick={login} disabled={loading}>{loading ? 'Entrando...' : '→ Entrar'}</Btn>
          </>
        )}
        {modoRecuperar && (
          <>
            <div style={{ background: C.primaryLt, borderRadius: 12, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: C.textMd, lineHeight: 1.6 }}>
              Te enviaremos un link a tu correo para crear una nueva contraseña.
            </div>
            <Btn onClick={recuperar} disabled={loading}>{loading ? 'Enviando...' : 'Enviar link de recuperación'}</Btn>
            <Btn onClick={() => setModoRecuperar(false)} variant="ghost" style={{ marginTop: 8 }}>← Volver al login</Btn>
          </>
        )}
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: C.textMd }}>
          ¿No tienes cuenta?{' '}
          <span onClick={onRegistro} style={{ color: C.primary, fontWeight: 700, cursor: 'pointer' }}>Regístrate gratis</span>
        </div>
      </Card>
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <span onClick={onLanding} style={{ fontSize: 15, fontWeight: 700, color: C.textMd, cursor: 'pointer' }}>← Volver al inicio</span>
      </div>
    </div>
  );
}


export { Login, Registro };
